// ============================================================================
// ATTACHMENT API TYPES - Digital Approval System
// ============================================================================
// Types for Attachment module matching backend DTOs

import { BaseEntity, ID } from './common.types';

// ============================================================================
// FILE SIZE LIMITS (in bytes)
// ============================================================================

/** Default maximum size for a single file: 100 KB (fallback if API unavailable) */
export const MAX_INDIVIDUAL_FILE_SIZE = 100 * 1024; // 100 KB

/** Default maximum total size for all attachments: 1 MB (fallback if API unavailable) */
export const MAX_TOTAL_ATTACHMENTS_SIZE = 1024 * 1024; // 1 MB

/** File size limit configuration */
export interface FileSizeLimits {
  maxIndividualSize: number;
  maxTotalSize: number;
  allowedExtensions?: string[];
  allowedExtensionsDisplay?: string;
}

/** Default file size limits (used as fallback if API settings unavailable) */
export const DEFAULT_FILE_SIZE_LIMITS: FileSizeLimits = {
  maxIndividualSize: MAX_INDIVIDUAL_FILE_SIZE,
  maxTotalSize: MAX_TOTAL_ATTACHMENTS_SIZE,
  allowedExtensions: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx'],
  allowedExtensionsDisplay: '.pdf, .jpg, .jpeg, .png, .doc, .docx, .xls, .xlsx',
};

/**
 * File upload settings from API (matches backend FileUploadSettingsDto)
 */
export interface FileUploadSettings {
  maxFileSizeKB: number;
  maxFileSizeBytes: number;
  maxTotalSizeKB: number;
  maxTotalSizeBytes: number;
  allowedExtensions: string[];
  allowedExtensionsDisplay: string;
}

/**
 * Converts API FileUploadSettings to FileSizeLimits
 */
export const toFileSizeLimits = (settings: FileUploadSettings): FileSizeLimits => ({
  maxIndividualSize: settings.maxFileSizeBytes,
  maxTotalSize: settings.maxTotalSizeBytes,
  allowedExtensions: settings.allowedExtensions,
  allowedExtensionsDisplay: settings.allowedExtensionsDisplay,
});

/** File size validation result */
export interface FileSizeValidationResult {
  isValid: boolean;
  error?: 'individual_limit_exceeded' | 'total_limit_exceeded';
  errorMessage?: string;
  fileSize: number;
  fileSizeFormatted: string;
  currentTotalSize: number;
  currentTotalSizeFormatted: string;
  newTotalSize: number;
  newTotalSizeFormatted: string;
}

/**
 * Attachment Entity (matches backend AttachmentDto)
 */
export interface Attachment extends BaseEntity {
  requestId: ID;
  workflowActionId?: ID;
  fileName: string;
  originalFileName: string;
  documentTitle: string;
  fileSizeBytes: number;
  fileSizeFormatted: string;
  fileType?: string;
  mimeType?: string;
  documentDescription?: string;
  uploadedByUserId: ID;
  uploadedByUserName?: string; // From joined data
  uploadedAt: string; // ISO datetime string
  downloadUrl?: string; // Generated URL
}

/**
 * Upload Attachment Request (matches backend UploadAttachmentRequest)
 */
export interface UploadAttachmentRequest {
  requestId: ID;
  documentTitle: string;
  documentDescription?: string;
  workflowActionId?: ID;
}

/**
 * Update Attachment Request (matches backend UpdateAttachmentRequest)
 */
export interface UpdateAttachmentRequest {
  documentTitle?: string;
  documentDescription?: string;
}

/**
 * Attachment Statistics
 */
export interface AttachmentStats {
  requestId: ID;
  attachmentCount: number;
  totalSizeBytes: number;
  totalSizeFormatted: string;
}

/**
 * File upload state for UI
 */
export interface FileUploadState {
  file: File;
  documentTitle: string;
  documentDescription?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  attachmentId?: ID;
}

// ============================================================================
// FILE SIZE UTILITY FUNCTIONS
// ============================================================================

/**
 * Formats bytes to human-readable file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

/**
 * Validates file size against individual and total limits
 * @param file - The file to validate
 * @param currentTotalSize - Current total size of all attachments in bytes
 * @param limits - Optional custom limits (defaults to DEFAULT_FILE_SIZE_LIMITS)
 * @returns FileSizeValidationResult with validation status and formatted sizes
 */
export const validateFileSize = (
  file: File,
  currentTotalSize: number = 0,
  limits: FileSizeLimits = DEFAULT_FILE_SIZE_LIMITS
): FileSizeValidationResult => {
  const fileSize = file.size;
  const newTotalSize = currentTotalSize + fileSize;

  const result: FileSizeValidationResult = {
    isValid: true,
    fileSize,
    fileSizeFormatted: formatFileSize(fileSize),
    currentTotalSize,
    currentTotalSizeFormatted: formatFileSize(currentTotalSize),
    newTotalSize,
    newTotalSizeFormatted: formatFileSize(newTotalSize),
  };

  // Check individual file size limit
  if (fileSize > limits.maxIndividualSize) {
    result.isValid = false;
    result.error = 'individual_limit_exceeded';
    result.errorMessage = `File size (${formatFileSize(fileSize)}) exceeds the maximum allowed size of ${formatFileSize(limits.maxIndividualSize)}.`;
    return result;
  }

  // Check total size limit
  if (newTotalSize > limits.maxTotalSize) {
    result.isValid = false;
    result.error = 'total_limit_exceeded';
    result.errorMessage = `Adding this file would exceed the total attachment limit of ${formatFileSize(limits.maxTotalSize)}. Current total: ${formatFileSize(currentTotalSize)}, File size: ${formatFileSize(fileSize)}.`;
    return result;
  }

  return result;
};

/**
 * Calculates total size of all files in an array
 * @param files - Array of File objects
 * @returns Total size in bytes
 */
export const calculateTotalFileSize = (files: File[]): number => {
  return files.reduce((total, file) => total + file.size, 0);
};
